import type { IconComponent } from 'uikit';
import {
    cnConsta,
    FieldCaption,
    FieldLabel,
    getByMap,
    IconClose,
    IconSelect,
    IconSelectOpen,
    useForkRef,
    usePropsHandler,
    useSortSteps,
} from 'uikit';

import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';

import type { TextFieldComponent, TextFieldProps } from './helpers';
import {
    getIncrementFlag,
    getValueByStepArray,
    getValueByStepNumber,
    sizeMap,
    textFieldPropFormDefault,
    textFieldPropSizeDefault,
    textFieldPropViewDefault,
    textFieldPropWidthDefault,
} from './helpers';

export const COMPONENT_NAME = 'TextField' as const;
export const cnTextField = cnConsta(COMPONENT_NAME);

// TODO
//  Очень страшная большая обертка, которая мало комментирована и сложно организована.
//  Так и просится рефакторинг, но в то же время, конечно, и трогать тоже не хочется.
export function TextFieldRender<TYPE extends string>(
    props: TextFieldProps<TYPE> & {
        texRight?: boolean;
        thousandSeparator?: boolean | string;
        labelIcon?: IconComponent;
    },
    ref: React.Ref<HTMLDivElement>,
) {
    const textFieldRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    const {
        className,
        type = 'text',
        value,
        onChange,
        id,
        name,
        rows,
        cols,
        minRows,
        maxRows,
        inputRef: inputRefProp,
        maxLength,
        disabled,
        size = textFieldPropSizeDefault,
        view = textFieldPropViewDefault,
        form = textFieldPropFormDefault,
        state,
        status,
        width = textFieldPropWidthDefault,
        onBlur,
        onFocus,
        autoFocus = false,
        placeholder,
        leftSide,
        rightSide,
        autoComplete,
        withClearButton,
        max,
        min,
        readOnly,
        required,
        step = 1,
        tabIndex,
        ariaLabel,
        label,
        inputContainerRef,
        isCustomTooltip = false,
        errorMessage,
        labelPosition = 'top',
        caption,
        iconSize: iconSizeProp,
        focused,
        onClick,
        texRight = false,
        decimalScale = 2,
        thousandSeparator = ' ',
        needSelectInputText = false,
        isCommitting = false,
        labelIcon,
        inputStyle,
        fixedDecimalScale = true,
        ...otherProps
    } = usePropsHandler(COMPONENT_NAME, props, textFieldRef);
    const [focus, setFocus] = useState<boolean>(autoFocus);
    const LeftIcon = leftSide;
    const RightIcon = rightSide;
    const leftSideIsString = typeof leftSide === 'string';
    const rightSideIsString = typeof rightSide === 'string';
    const iconSize = getByMap(sizeMap, size, iconSizeProp);

    const sortedSteps = useSortSteps({ step, min: Number(min), max: Number(max) });

    const handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
        if (!disabled) {
            onChange?.({ e, id, name, value: e.target.value || null });
        }
    };

    const handleBlur: React.FocusEventHandler<HTMLElement> = (e) => {
        setFocus(false);
        onBlur?.(e);
    };

    const handleFocus: React.FocusEventHandler<HTMLElement> = (e) => {
        setFocus(true);
        onFocus?.(e);
    };

    const commonProps = {
        className: cnTextField('Input'),
        value: value || '',
        onChange: handleChange,
        maxLength,
        disabled,
        onBlur: handleBlur,
        onFocus: handleFocus,
        autoFocus,
        placeholder,
        autoComplete,
        readOnly,
        required,
        tabIndex,
        name,
        id: id ? id.toString() : '',
        'aria-label': ariaLabel,
    };

    useEffect(() => {
        if (autoFocus) {
            setTimeout(() => {
                inputRef.current?.focus();
            });
        }
    }, [autoFocus]);

    useEffect(() => {
        if (autoFocus && needSelectInputText) {
            const t = setTimeout(() => {
                if (value && value.length > 1) {
                    clearTimeout(t);

                    return;
                }
                const textInput = textFieldRef.current?.querySelector('input');

                textInput?.setRangeText('', 1, value?.length || 16);
            }, 0);
        }
    }, [autoFocus, needSelectInputText, value]);

    const onKeyDown = (e: React.KeyboardEvent) => {
        const flag = getIncrementFlag(e);

        if (isCommitting) {
            e.preventDefault();

            return;
        }

        if (type === 'number' && typeof flag === 'boolean' && !disabled) {
            e.preventDefault();
            onChange?.({
                e,
                value: getValueByStep(flag).toString(),
            });
        }
    };

    const inputProps = {
        type,
        max,
        min,
        step: !Array.isArray(sortedSteps) ? sortedSteps : 0,
        onKeyDown,
        // ref: useForkRef([inputRef, inputRefProp]),
    };

    const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
        onChange?.({
            e,
            value: '',
        });
    };

    const getValueByStep = (isIncrement?: boolean) =>
        Array.isArray(sortedSteps)
            ? getValueByStepArray({
                  min,
                  max,
                  value,
                  isIncrement,
                  steps: sortedSteps,
              }) ?? 0
            : getValueByStepNumber({ value, step: sortedSteps, isIncrement, min, max });

    const changeNumberValue = (e: React.MouseEvent<HTMLButtonElement>, isIncrement = true) => {
        onChange?.({
            e,
            value: getValueByStep(isIncrement).toString(),
        });
    };

    const rootProps = {
        // для того чтобы любые клики во внутренним элементам фокусили инпут
        onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            inputRef.current?.focus();
            onClick?.(e);
        },
    };

    const classNameNumeric = texRight ? 'TextField-Input texRight' : 'TextField-Input';

    return (
        <div
            className={cnTextField({ labelPosition, size, view, width }, [className])}
            ref={useForkRef([ref, textFieldRef])}
            {...rootProps}
            {...otherProps}
        >
            {label && (
                <FieldLabel required={required} className={cnTextField('Label', { labelPosition })} size={size}>
                    {label}
                    {labelIcon && React.createElement(labelIcon)}
                </FieldLabel>
            )}
            <div className={cnTextField('Body')}>
                <div
                    ref={inputContainerRef}
                    className={cnTextField('InputContainer', {
                        view,
                        form,
                        status: status || state,
                        disabled,
                        type,
                        focus: focus || focused,
                        withValue: !!value,
                    })}
                >
                    {LeftIcon && (
                        <div
                            className={cnTextField('Side', {
                                position: 'left',
                                type: leftSideIsString ? 'string' : 'icon',
                            })}
                        >
                            {leftSideIsString ? leftSide : <LeftIcon className={cnTextField('Icon')} size={iconSize} />}
                        </div>
                    )}
                    <NumericFormat
                        {...commonProps}
                        {...inputProps}
                        type="text"
                        thousandSeparator={thousandSeparator}
                        decimalSeparator=","
                        decimalScale={decimalScale}
                        fixedDecimalScale={fixedDecimalScale}
                        className={classNameNumeric}
                        style={inputStyle}
                    />

                    {type === 'number' && (
                        <div className={cnTextField('Counter')}>
                            <button
                                onFocus={handleFocus}
                                onClick={(e) => changeNumberValue(e, true)}
                                type="button"
                                className={cnTextField('CounterButton')}
                            >
                                <IconSelectOpen size="xs" />
                            </button>
                            <button
                                onFocus={handleFocus}
                                onClick={(e) => changeNumberValue(e, false)}
                                type="button"
                                className={cnTextField('CounterButton')}
                            >
                                <IconSelect size="xs" />
                            </button>
                        </div>
                    )}

                    {value && withClearButton && type !== 'number' && (
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={handleClear}
                            className={cnTextField('ClearButton')}
                        >
                            <IconClose size="xs" className={cnTextField('ClearButtonIcon')} />
                        </button>
                    )}

                    {RightIcon && type !== 'number' && (
                        <div
                            className={cnTextField('Side', {
                                position: 'right',
                                type: rightSideIsString ? 'string' : 'icon',
                            })}
                            title={typeof rightSide === 'string' ? rightSide : undefined}
                        >
                            {rightSideIsString ? (
                                rightSide
                            ) : (
                                <RightIcon className={cnTextField('Icon')} size={iconSize} />
                            )}
                        </div>
                    )}
                </div>
                {isCustomTooltip && <div className="customStaticTooltip">{errorMessage}</div>}
                {caption && (
                    <FieldCaption className={cnTextField('Caption')} status={status || state}>
                        {caption}
                    </FieldCaption>
                )}
            </div>
        </div>
    );
}

export const FieldNumberFormat = forwardRef(TextFieldRender) as TextFieldComponent;
export * from './helpers';

TextFieldRender.displayName = 'FieldNumberFormat';
